import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle2, Play, Lock, ChevronDown, AlertTriangle, Zap,
  Headphones, Film, Brain, FileText, Layers, BarChart3, Image, Table2, ClipboardList, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const MATERIAL_ICONS = {
  audio_url: { icon: Headphones, label: 'Sesli Ozet', color: 'text-indigo-400 bg-indigo-500/10' },
  video_url: { icon: Film, label: 'Video', color: 'text-pink-400 bg-pink-500/10' },
  mindmap_url: { icon: Brain, label: 'Zihin Haritasi', color: 'text-purple-400 bg-purple-500/10' },
  report_content: { icon: FileText, label: 'Rapor', color: 'text-slate-400 bg-slate-500/10' },
  flashcards: { icon: Layers, label: 'Bilgi Kartlari', color: 'text-amber-400 bg-amber-500/10' },
  extra_quiz: { icon: ClipboardList, label: 'Ek Test', color: 'text-cyan-400 bg-cyan-500/10' },
  infographic_urls: { icon: Image, label: 'Infografik', color: 'text-emerald-400 bg-emerald-500/10' },
  slides_url: { icon: BarChart3, label: 'Slayt', color: 'text-blue-400 bg-blue-500/10' },
  data_tables: { icon: Table2, label: 'Veri Tablosu', color: 'text-orange-400 bg-orange-500/10' },
};

function getMaterialTags(materials) {
  if (!materials) return [];
  const tags = [];
  for (const [key, config] of Object.entries(MATERIAL_ICONS)) {
    const val = materials[key];
    if (val && (typeof val === 'string' ? val.length > 0 : Array.isArray(val) ? val.length > 0 : false)) {
      let extra = '';
      if (key === 'flashcards') extra = ` ${val.length}`;
      if (key === 'infographic_urls') extra = ` ${val.length}`;
      tags.push({ key, ...config, extra });
    }
  }
  return tags;
}

export default function ModulePage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({});
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [lessonDetails, setLessonDetails] = useState({});
  const [examInfo, setExamInfo] = useState(null);

  const loadLessonDetail = async (lessonId) => {
    if (lessonDetails[lessonId]) return;
    try {
      const res = await fetch(`${API}/api/lessons/${lessonId}`, { headers: H() });
      if (res.ok) {
        const data = await res.json();
        setLessonDetails(prev => ({ ...prev, [lessonId]: data }));
      }
    } catch {}
  };

  const toggleExpand = (lessonId, isLocked) => {
    if (isLocked) return;
    const newId = expandedLesson === lessonId ? null : lessonId;
    setExpandedLesson(newId);
    if (newId) loadLessonDetail(newId);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [moduleId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = H();
      const [fullRes, examRes] = await Promise.all([
        fetch(`${API}/api/modules/${moduleId}/full`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/exams/available`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      
      if (fullRes) {
        setModule(fullRes.module);
        setLessons(fullRes.lessons || []);
        const pMap = {};
        for (const l of fullRes.lessons || []) {
          if (l.progress) pMap[l.id] = l.progress;
        }
        setProgress(pMap);
      }

      if (examRes?.module_exams) {
        setExamInfo(examRes.module_exams.find(e => e.module_id === moduleId));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!module) return <div className="p-8 text-center text-muted-foreground">Egitim bulunamadi</div>;

  const completedCount = lessons.filter(l => progress[l.id]?.is_completed).length;
  const totalCount = lessons.length;
  const pct = totalCount > 0 ? Math.round(completedCount / totalCount * 100) : 0;

  // Find next lesson
  const nextLesson = lessons.find(l => !progress[l.id]?.is_completed);
  const nextIdx = nextLesson ? lessons.indexOf(nextLesson) : -1;

  return (
    <div data-testid="education-detail">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="px-6 py-3.5 max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center">
              {pct === 100 ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Play className="w-5 h-5 text-primary" />}
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground/40">{moduleId}</div>
              <h1 className="text-base font-bold text-foreground">{module.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-extrabold text-primary">%{pct}</span>
            <div className="w-24 h-1.5 rounded-full bg-muted/50">
              <div className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 py-5 max-w-[1200px] mx-auto space-y-1.5">
        {/* Lesson List */}
        {lessons.map((lesson, idx) => {
          const prog = progress[lesson.id];
          const isCompleted = prog?.is_completed;
          const isCurrent = idx === nextIdx;
          const isLocked = idx > nextIdx && nextIdx >= 0;
          const quizScore = prog?.quiz_score;
          const quizPassed = prog?.quiz_passed;
          const isWeak = isCompleted && !quizPassed;
          const isExpanded = expandedLesson === lesson.id;
          const materials = getMaterialTags(lessonDetails[lesson.id]?.materials || lesson.materials);

          return (
            <div key={lesson.id}
              className={`rounded-xl transition-all ${
                isCurrent ? 'bg-primary/[0.03] border-l-[3px] border-l-primary' :
                isWeak ? 'border-l-[3px] border-l-amber-500' :
                isCompleted ? 'border-l-[3px] border-l-emerald-500/50' :
                isLocked ? 'border-l-[3px] border-l-transparent opacity-40' :
                'border-l-[3px] border-l-transparent'
              }`}
              data-testid={`lesson-${lesson.id}`}
            >
              {/* Lesson Row */}
              <div className={`px-4 py-3 flex items-center gap-3 ${!isLocked ? 'cursor-pointer hover:bg-muted/20' : ''}`}
                onClick={() => {
                  if (isLocked) return;
                  navigate(`/lesson/${lesson.id}`);
                }}
              >
                {/* Status Icon */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                  isCompleted && !isWeak ? 'bg-emerald-500/10' :
                  isWeak ? 'bg-amber-500/10' :
                  isCurrent ? 'bg-primary/12 border border-primary/20' :
                  'bg-muted/30'
                }`}>
                  {isCompleted && !isWeak ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> :
                   isWeak ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> :
                   isCurrent ? <span className="text-primary text-[11px] font-bold">{idx + 1}</span> :
                   isLocked ? <Lock className="w-3 h-3 text-muted-foreground/20" /> :
                   <span className="text-muted-foreground/20 text-[11px]">{idx + 1}</span>}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-foreground/80">{lesson.name}</div>
                  <div className="text-[10px] text-muted-foreground/30 mt-0.5">
                    {lesson.duration_minutes || 25} dk
                    {isCompleted && !isWeak && <span className="text-emerald-400/60"> • Tamamlandı</span>}
                    {isWeak && <span className="text-amber-400/60"> • Tekrar önerilir</span>}
                  </div>
                </div>

                {/* Right side */}
                {isCurrent && (
                  <Button size="sm" className="shrink-0" onClick={(e) => { e.stopPropagation(); navigate(`/lesson/${lesson.id}`); }}>Basla <ArrowRight className="w-3 h-3 ml-1" /></Button>
                )}
                {isCompleted && !isCurrent && (
                  <Button size="sm" variant="outline" className="shrink-0 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/lesson/${lesson.id}`); }}>Tekrar</Button>
                )}
                {(materials.length > 0 || isWeak) && !isLocked && (
                  <ChevronDown className={`w-4 h-4 text-muted-foreground/15 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                )}
              </div>

              {/* Expanded: Materials + Weak Warning */}
              {isExpanded && (
                <div className="px-4 pb-3.5 pl-14 space-y-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  {/* Material Tags */}
                  {materials.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {materials.map(m => {
                        const Icon = m.icon;
                        return (
                          <button key={m.key} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all hover:scale-[1.02] ${m.color}`}>
                            <Icon className="w-3 h-3" />{m.label}{m.extra}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Weak Warning */}
                  {isWeak && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/[0.03] border border-amber-500/10">
                      <Zap className="w-4 h-4 text-amber-400 shrink-0" />
                      <div className="flex-1">
                        <div className="text-[11px] font-semibold text-amber-300/90">Zayif konular tespit edildi</div>
                        <div className="text-[9px] text-muted-foreground/30">Quiz'de hata yaptigin konulari kartlarla tekrar et</div>
                      </div>
                      <Button size="sm" variant="outline" className="shrink-0 text-amber-400 border-amber-500/15 hover:bg-amber-500/5 text-[10px]">
                        Kartlari Calis →
                      </Button>
                    </div>
                  )}

                  {/* Go to lesson */}
                  {isCompleted && (
                    <Button variant="ghost" size="sm" className="text-[10px] text-muted-foreground/40" onClick={() => navigate(`/lesson/${lesson.id}`)}>
                      Dersi tekrar ac →
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Exam Card */}
        <Card className="mt-4" data-testid="exam-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-lg">📝</span>
              <div>
                <div className="text-[13px] font-semibold text-foreground/80">{module.name} Sinavi</div>
                <div className="text-[10px] text-muted-foreground/30">
                  {examInfo?.question_count || 25} soru • {examInfo?.duration_minutes || 30} dk • %{examInfo?.passing_score || 80} gecme
                </div>
              </div>
            </div>
            {examInfo?.is_eligible ? (
              <Button size="sm" onClick={() => navigate(`/exams?start=${examInfo.exam_id}`)}>Sinava Gir →</Button>
            ) : (
              <span className="text-[10px] text-muted-foreground/20">Tum dersler tamamlaninca acilir</span>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
